import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { signalStore } from '@ngrx/signals';
import { skip, debounceTime, Observable } from 'rxjs';
import { InsightsService, TrackingEvent } from 'shared/domain';
import {
  InputDirective,
  IconDirective,
  InputPrefixDirective,
  FormFieldImports,
  PopoverRef,
  PopoverService,
  CheckboxComponent,
} from 'shared/ui-common';
import { IsMobileService, ServerResponse, withPagedList } from 'shared/util-common';

import { Guideline } from '@treetop/shared/model';

import { AttachmentSearchResultListComponent } from './attachment-search-result-list/attachment-search-result-list.component';
import { AttachmentSearchHttpService } from './attachment-search.http.service';
import { AttachmentFullTextFilter, AttachmentSearchResult } from './attachment-search.model';
import { GuidelineSearchResultItemSelectedEvent } from './guideline-search-result-item/guideline-search-result-item.component';
import { GuidelineSearchResultListComponent } from './guideline-search-result-list/guideline-search-result-list.component';
import { GuidelineSearchHttpService } from './guideline-search.http.service';
import { GuidelineFullTextFilter } from './guideline-search.model';

export const GuidelineSearchStore = signalStore(withPagedList());
export const AttachmentSearchStore = signalStore(withPagedList());

@Component({
  standalone: true,
  imports: [
    InputDirective,
    TranslocoModule,
    ReactiveFormsModule,
    MatIconModule,
    IconDirective,
    GuidelineSearchResultListComponent,
    AttachmentSearchResultListComponent,
    FormsModule,
    InputPrefixDirective,
    FormFieldImports,
    CheckboxComponent,
  ],
  selector: 'tt-search',
  templateUrl: './tt-search.component.html',
  styleUrls: ['./tt-search.component.scss'],
  providers: [GuidelineSearchStore, AttachmentSearchStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent {
  private guidelineSearchStore = inject(GuidelineSearchStore);
  private attachmentSearchStore = inject(AttachmentSearchStore);
  private guidelineSearchHttpService = inject(GuidelineSearchHttpService);
  private attachmentSearchHttpService = inject(AttachmentSearchHttpService);
  private elementRef = inject(ElementRef);
  private viewContainerRef = inject(ViewContainerRef);
  private popoverService = inject(PopoverService);
  private isMobileService = inject(IsMobileService);
  private router = inject(Router);
  private insightsService = inject(InsightsService);

  public headerRef = input<ElementRef<HTMLElement>>();

  public disabled = input<boolean>(false);

  @HostBinding('class.tt-search--focused')
  public get focusedClass() {
    return this.isFocused();
  }

  public resultDropdown = viewChild.required<TemplateRef<unknown>>('resultDropdown');
  public searchTermSpan = viewChild.required<ElementRef<HTMLSpanElement>>('searchTermSpan');

  protected isFocused = signal(false);
  protected searchTerm = signal<string>('');
  protected activeTab = signal<'guidelines' | 'attachments'>('guidelines');
  protected isHomeFilterActive = signal(false);
  protected guidelineVm = this.guidelineSearchStore.viewModel;
  protected attachmentVm = this.attachmentSearchStore.viewModel;
  protected popoverRef: PopoverRef;

  protected guidelineSearchGetFilter = computed<{ filter: GuidelineFullTextFilter }>(() => {
    const filter: GuidelineFullTextFilter = {
      searchText: this.searchTerm(),
      taxonomyIds: [],
    };

    return { filter };
  });

  protected attachmentSearchGetFilter = computed<{ filter: AttachmentFullTextFilter }>(() => {
    const filter: AttachmentFullTextFilter = {
      searchText: this.searchTerm(),
    };

    return { filter };
  });

  private searchTrigger = computed(() => ({
    searchTerm: this.searchTerm(),
    isHomeActive: this.isHomeFilterActive(),
  }));

  constructor() {
    this.guidelineSearchStore.init({
      serverCall: (page) => this.searchGuidelines(page),
      loadPageInitially: false,
    });

    this.attachmentSearchStore.init({
      serverCall: (page) => this.searchAttachments(page),
      loadPageInitially: false,
    });

    toObservable(this.searchTrigger)
      .pipe(takeUntilDestroyed(), skip(1), debounceTime(330))
      .subscribe(() => {
        if (this.isSearchTermValid()) {
          this.trackSearchUsage(this.searchTerm());
          this.showSearchResults();
          this.guidelineSearchStore.loadPage(1, 10);
          this.attachmentSearchStore.loadPage(1, 10);
        } else {
          this.closeSearchResults();
        }
      });
  }

  // The endpoint throws an error if the search text is less than 2 characters.
  private isSearchTermValid = computed(() => {
    const term = this.searchTerm();
    return !!term && term.trim().length > 1;
  });

  protected switchTab(tab: 'guidelines' | 'attachments') {
    this.activeTab.set(tab);
  }

  protected handleSearchTermChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    this.activeTab.set('guidelines');
  }

  protected onInputFocus() {
    this.isFocused.set(true);
    if (this.isSearchTermValid()) {
      this.showSearchResults();
    }
  }

  protected onInputBlur() {
    if (!this.popoverRef) {
      this.isFocused.set(false);
    }
  }

  protected onClearSearch() {
    this.searchTerm.set('');
    this.closeSearchResults();
  }

  private showSearchResults(): void {
    if (this.popoverRef) {
      return;
    }
    if (this.isMobileService.isMobile()) {
      this.popoverRef = this.popoverService.open(
        this.resultDropdown(),
        this.headerRef().nativeElement,
        {
          offsetY: 100,
          width: '100%',
          maxWidth: '100%',
          height: `calc(100% - ${this.headerRef().nativeElement.offsetHeight}px)`,
          ignoreElement: this.searchTermSpan().nativeElement,
          hasBackdrop: false,
          disableClose: false,
          data: {
            searchTerm: this.searchTerm,
            guidelineVm: this.guidelineVm,
            attachmentVm: this.attachmentVm,
          },
        },
        this.viewContainerRef,
      );
    } else {
      this.popoverRef = this.popoverService.open(
        this.resultDropdown(),
        this.elementRef.nativeElement,
        {
          offsetY: 8,
          width: 550,
          maxHeight: 400,
          positionStrategy: 'bottom-right',
          ignoreElement: this.searchTermSpan().nativeElement,
          hasBackdrop: false,
          data: {
            searchTerm: this.searchTerm,
            guidelineVm: this.guidelineVm,
            attachmentVm: this.attachmentVm,
          },
        },
        this.viewContainerRef,
      );
    }
    this.popoverRef.afterClosed().subscribe(() => {
      this.popoverRef = null;
    });
  }

  protected onLoadMoreGuidelines() {
    if (this.guidelineSearchStore.viewModel().hasMore && !this.guidelineSearchStore.viewModel().loading) {
      this.guidelineSearchStore.loadNextPage();
    }
  }

  protected onLoadMoreAttachments() {
    if (this.attachmentSearchStore.viewModel().hasMore && !this.attachmentSearchStore.viewModel().loading) {
      this.attachmentSearchStore.loadNextPage();
    }
  }

  protected onGuidelineSelected(event: GuidelineSearchResultItemSelectedEvent) {
    this.trackGuidelineSearchResultClick(event, this.searchTerm());

    if (event.uniqueId && event.matchingFieldUniqueId && event.goToPath) {
      this.router.navigate(['/guideline', event.uniqueId], { fragment: event.matchingFieldUniqueId });
    } else if (event.uniqueId) {
      this.router.navigate(['/guideline', event.uniqueId]);
    }

    this.closeSearchResults();
  }

  protected onAttachmentSelected(event: AttachmentSearchResult) {
    this.router.navigate(['/smartbook/assets'], { queryParams: { searchText: event.displayName } });
    this.closeSearchResults();
  }

  protected onHomeFilterActiveChanged(isActive: boolean) {
    this.isHomeFilterActive.set(isActive);
  }

  private closeSearchResults(): void {
    if (this.popoverRef) {
      this.popoverRef.close();
      this.popoverRef = null;
    }
  }

  private searchGuidelines(page: number): Observable<ServerResponse<Guideline>> {
    return this.guidelineSearchHttpService.getSearchResult(this.guidelineSearchGetFilter().filter, page, this.isHomeFilterActive());
  }

  private searchAttachments(page: number): Observable<ServerResponse<AttachmentSearchResult>> {
    return this.attachmentSearchHttpService.getSearchResult(this.attachmentSearchGetFilter().filter, page, this.isHomeFilterActive());
  }

  private trackSearchUsage(searchTerm: string): void {
    this.insightsService.trackEvent(TrackingEvent.GUIDELINE_SEARCH, {
      searchTerm: searchTerm,
      searchLength: searchTerm.length,
      timestamp: new Date().toISOString(),
    });
  }

  private trackGuidelineSearchResultClick(item: GuidelineSearchResultItemSelectedEvent, searchTerm: string): void {
    this.insightsService.trackEvent(TrackingEvent.GUIDELINE_SEARCH_RESULT_CLICK, {
      searchTerm: searchTerm,
      guidelineId: item.uniqueId,
      matchingFieldType: item.matchingField,
      goToPath: item.goToPath,
      resultPosition: item.resultPosition,
      totalResults: this.guidelineVm().paging.maxElements,
      score: item.score,
      timestamp: new Date().toISOString(),
    });
  }
}
